import { DATABRICKS_COMING_SOON_MESSAGE } from "./destinationReadiness";

/**
 * #923: what a destination test (`POST /instances/current/binding/test`) answers, by code. Each
 * code has a fixed sentence and a next step; the grant codes also have the SQL a Snowflake admin
 * runs. Never the driver's text (ADR 0011, `runner/steps/sanitiseError.ts` `driverErrorView`):
 * the runner classifies the error in memory and answers a code, the driver's error code and the
 * query id only.
 *
 * The SQL lines follow `docs/deploy/snowflake-loader-least-privilege.md`. They are rendered on the
 * server (`server/services/destinationTest.ts`) with names the identifier rule accepted; the role
 * and the user read `<loader role>` and `<loader user>` for a caller without
 * `instance.binding.identity.read`.
 */

export const WAREHOUSE_TEST_CODES = [
  "ok",
  "incomplete",
  "key_unreadable",
  "unavailable",
  "unreachable",
  "sign_in_refused",
  "role_not_granted",
  "warehouse_unusable",
  "database_unusable",
  "schema_create_denied",
  "schema_unusable",
  "schema_missing",
  "timeout",
  "failed",
  "stale",
  "not_supported",
] as const;
export type WarehouseTestCode = (typeof WAREHOUSE_TEST_CODES)[number];

/**
 * Codes that are not an answer about the destination: the test could not run, or the binding
 * changed while it ran. They never move the destination's state (design review change 6).
 */
export const TRANSIENT_TEST_CODES: ReadonlySet<WarehouseTestCode> = new Set([
  "unavailable",
  "timeout",
  "stale",
  "not_supported",
]);

/** The names a SQL line uses, each already in the form a statement takes (or a placeholder). */
export interface AdviceNames {
  role: string;
  user: string;
  warehouse: string | null;
  database: string;
  schema: string;
}

export interface TestAdvice {
  sentence: string;
  nextStep: string | null;
  sql?: (names: AdviceNames) => string[];
}

const NETWORK_NEXT_STEP =
  "Check the account identifier (orgname-account). If the account or the user has a network policy, allow skyData's outbound address (ask Skypoint for it).";

/**
 * The most addresses the network next step names inline. A longer list would be an unreadable
 * sentence, so it keeps "ask Skypoint" instead (#1720 review).
 */
export const NETWORK_NEXT_STEP_MAX_ADDRESSES = 4;

/**
 * The network next step with the address a Snowflake network policy must allow, when the web knows
 * a stable one (`OutboundAddresses.networkPolicyAddresses`: firewall egress's hub firewall public
 * address from the environment file, #1720; a deployment that reads Azure passes none). None known,
 * or more than `NETWORK_NEXT_STEP_MAX_ADDRESSES`: ask Skypoint, never an address that may be wrong
 * and never a wall of addresses.
 */
export function networkNextStep(addresses: readonly string[]): string {
  if (addresses.length === 0 || addresses.length > NETWORK_NEXT_STEP_MAX_ADDRESSES) {
    return NETWORK_NEXT_STEP;
  }
  const list = addresses.join(", ");
  return addresses.length === 1
    ? `Check the account identifier (orgname-account). If the account or the user has a network policy, allow this address in your Snowflake network policy: ${list}.`
    : `Check the account identifier (orgname-account). If the account or the user has a network policy, allow these addresses in your Snowflake network policy: ${list}.`;
}

/** The codes whose next step is the network one (`networkNextStep`). */
export const NETWORK_TEST_CODES: ReadonlySet<WarehouseTestCode> = new Set(["unreachable"]);

export const DESTINATION_TEST_ADVICE: Record<WarehouseTestCode, TestAdvice> = {
  ok: {
    sentence: "skyData signed in to Snowflake and can use the warehouse, database and schema.",
    nextStep: null,
  },
  incomplete: {
    sentence: "The destination is missing a field a load needs.",
    nextStep: "Fill in the fields, then Save and test.",
  },
  key_unreadable: {
    sentence: "The saved private key could not be read or used.",
    nextStep: "Check the private key and its passphrase. Paste them again, then Save and test.",
  },
  unavailable: {
    sentence: "The test could not run.",
    nextStep: "Try again in a minute.",
  },
  unreachable: {
    sentence: "skyData could not reach Snowflake at this account.",
    nextStep: NETWORK_NEXT_STEP,
  },
  sign_in_refused: {
    // #1730: Snowflake answers a user it does not know and a key that does not match the user's
    // public key alike (390144), so the sentence names both fields.
    sentence: "skyData reached Snowflake but could not sign in as this user.",
    nextStep:
      "Check the User and the private key. The public key set on the user must match this private key (compare RSA_PUBLIC_KEY_FP).",
    sql: (n) => [
      `DESC USER ${n.user};`,
      `ALTER USER ${n.user} SET RSA_PUBLIC_KEY = '<public key>';`,
    ],
  },
  role_not_granted: {
    // #1730: 390189 is a role that does not exist and a role not granted to the user alike.
    sentence:
      "skyData reached Snowflake but the Role does not exist or is not granted to this user.",
    nextStep: "Check the Role. If it is right, ask your Snowflake admin to grant it to the user.",
    sql: (n) => [`GRANT ROLE ${n.role} TO USER ${n.user};`],
  },
  warehouse_unusable: {
    sentence: "The role cannot use the warehouse, or no warehouse is set.",
    nextStep:
      "Set a warehouse, or a default warehouse on the user, and grant the role USAGE on it.",
    sql: (n) => (n.warehouse ? [`GRANT USAGE ON WAREHOUSE ${n.warehouse} TO ROLE ${n.role};`] : []),
  },
  database_unusable: {
    sentence: "The database does not exist, or the role cannot use it.",
    nextStep: "Ask your Snowflake admin to create the database and grant the role on it.",
    sql: (n) => [
      `CREATE DATABASE IF NOT EXISTS ${n.database};`,
      `GRANT USAGE, CREATE SCHEMA ON DATABASE ${n.database} TO ROLE ${n.role};`,
    ],
  },
  schema_create_denied: {
    sentence: "The role cannot create the schema.",
    nextStep:
      "Grant the role CREATE SCHEMA on the database, or create the schema and grant the role USAGE on it.",
    sql: (n) => [`GRANT CREATE SCHEMA ON DATABASE ${n.database} TO ROLE ${n.role};`],
  },
  schema_unusable: {
    sentence: "The schema exists but the role cannot use it.",
    nextStep: "Ask your Snowflake admin to grant the role USAGE on the schema.",
    sql: (n) => [`GRANT USAGE ON SCHEMA ${n.database}.${n.schema} TO ROLE ${n.role};`],
  },
  schema_missing: {
    sentence: "The schema does not exist. The next import creates it.",
    nextStep: null,
  },
  timeout: {
    sentence: "Snowflake did not answer in time.",
    nextStep:
      "Try again. If it keeps happening, check the account identifier and the network policy.",
  },
  failed: {
    sentence: "The test failed.",
    nextStep: "Send the code and the query id to Skypoint support.",
  },
  stale: {
    sentence: "The destination changed while it was tested.",
    nextStep: "Test again.",
  },
  not_supported: {
    sentence: DATABRICKS_COMING_SOON_MESSAGE,
    nextStep: null,
  },
};

/** For a reader below `instance.binding.identity.read`: the page hides the user and the role. */
export const IDENTITY_WITHHELD_NEXT_STEP =
  "Ask a tenant admin or a Skypoint platform admin to check the sign-in details.";

/** For a reader who cannot change the key: only a platform admin can. */
export const KEY_WITHHELD_NEXT_STEP = "Ask a Skypoint platform admin to check the private key.";

/** Said beside Test (#923 review change 12): the test may create the load schema. */
export const TEST_CREATES_SCHEMA_NOTE =
  "Test creates the schema if it does not exist, as the first import would.";

/** The placeholders a withheld loader identity reads as in a SQL line. */
export const LOADER_ROLE_PLACEHOLDER = "<loader role>";
export const LOADER_USER_PLACEHOLDER = "<loader user>";
